// ═══════════════════════════════════════════════
//   DB — thin wrapper around the Supabase client.
//   Loaded after data.js/state.js, before bootstrap.js.
//
//   Each method passes through Supabase's native return shape
//   ({data, error} or {data:{session}}) so callers can destructure
//   exactly as before. The wrapper centralises the client reference
//   so future cross-cutting concerns (retry, caching, telemetry)
//   live in one place.
// ═══════════════════════════════════════════════

const {S, CONFIG} = window;

const DB = (() => {
    let _client = null;
    try {
        _client = supabase.createClient(
            'https://rypdlmdranhgxzjkyutb.supabase.co',
            'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5cGRsbWRyYW5oZ3h6amt5dXRiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMTgxODYsImV4cCI6MjA5MjY5NDE4Nn0.daosi2O8hGwZIHH1jfma50h1DxZEIVj7Rx5asiUbi78'
        );
    } catch (e) {
        console.error('Supabase CDN not loaded — check network:', e);
    }

    return {
        // Raw client (escape hatch — avoid using directly).
        get client() { return _client; },
        available() { return !!_client; },

        // ── Auth ──
        getSession()           { return _client.auth.getSession(); },
        signUp(payload)        { return _client.auth.signUp(payload); },
        signIn(payload)        { return _client.auth.signInWithPassword(payload); },
        signOut()              { return _client.auth.signOut(); },

        // ── Profile ──
        loadProfile(userId)    { return _client.from('profiles').select('*').eq('id', userId).single(); },
        upsertProfile(payload) { return _client.from('profiles').upsert(payload); },

        // ── Missions ──
        // Rows with active_date = NULL are evergreen; rows with a date are
        // daily and only appear on that calendar day (UTC).
        fetchMissions() {
            const today = new Date().toISOString().split('T')[0];
            return _client.from('missions').select('*').eq('active', true)
                .or(`active_date.is.null,active_date.eq.${today}`)
                .order('active_date', { ascending: true, nullsFirst: true })
                .order('sort_order');
        },

        // ── Leaderboard ──
        fetchLeaderboard()     { return _client.rpc('get_leaderboard'); },

        // ── Shop ──
        fetchShopItems()       { return _client.from('shop_items').select('*').order('sort_order'); },

        // ── Server state (maintenance flag, single-row table) ──
        fetchServerState()     { return _client.from('server_state').select('*').eq('id', 1).maybeSingle(); },

        // ── Sidecar table reads (used by loadProfile to hydrate localOnly fields) ──
        fetchUserScores(userId)   { return _client.from('user_game_scores').select('game_key, score').eq('user_id', userId); },
        fetchUserUnlocks(userId)  { return _client.from('user_unlocks').select('unlock_type, unlock_key').eq('user_id', userId); },
        fetchUserMissions(userId) { return _client.from('user_missions').select('mission_key, link_opened_at, completed_at').eq('user_id', userId); },
        fetchUserAliens(userId)   { return _client.from('user_aliens').select('*').eq('user_id', userId); },
        fetchUserToys(userId)     { return _client.from('user_toys').select('*').eq('user_id', userId); },
        fetchUserIrlCodes(userId) { return _client.from('user_irl_codes').select('*').eq('user_id', userId); },
        fetchUserMail(userId)     { return _client.from('user_mail_state').select('*').eq('user_id', userId).maybeSingle(); },
        fetchUserBakery(userId)   { return _client.from('user_bakery_state').select('*').eq('user_id', userId).maybeSingle(); },
        fetchUserTama(userId)     { return _client.from('user_tama_state').select('*').eq('user_id', userId).maybeSingle(); },
        fetchUserQuiz(userId)     { return _client.from('user_quiz_state').select('*').eq('user_id', userId).maybeSingle(); },
        fetchUserDaily(userId)    { return _client.from('user_daily_state').select('*').eq('user_id', userId).maybeSingle(); },
        fetchUserLogbookFavorites(userId) { return _client.from('user_logbook').select('alien_id').eq('user_id', userId); },
        fetchUserBunnymeals(userId)       { return _client.from('user_bunnymeals').select('*').eq('user_id', userId); },

        // ── Single-row sub-state writes (one upsert per save) ──
        syncUserMail(userId, mail) {
            return _client.from('user_mail_state').upsert({
                user_id: userId,
                first_open_at: mail.firstOpen || null,
                story_read: mail.storyRead || [],
                story_choices: mail.storyChoices || {},
                story_trashed: mail.storyTrashed || [],
                daily_state: mail.dailyState || {},
                important_trashed: mail.importantTrashed || [],
                spam_state: mail.spamState || {},
                shop_health:     mail.shopHealth     ?? 70,
                clow_awareness:  mail.clowAwareness  ?? 0,
                mystery_depth:   mail.mysteryDepth   ?? 0,
                records_kept:    mail.recordsKept    ?? 0,
                trust_hideo:     mail.trustHideo     || 'mid',
                trust_petra:     mail.trustPetra     || 'mid',
                last_active_day: mail.lastActiveDay  ?? 0,
                first_login_day: mail.firstLoginDay  ?? null,
                email_states:    mail.emailStates    || {},
                ending_key:      mail.ending         || null,
                voice_warm:           mail.voiceWarm           ?? 0,
                voice_curious:        mail.voiceCurious        ?? 0,
                voice_professional:   mail.voiceProfessional   ?? 0,
                voice_clipped:        mail.voiceClipped        ?? 0,
                clipped_recovery:     !!mail.clippedRecovery,
                emorrow_surfaced:     !!mail.eMorrowSurfaced,
                documented_path:      !!mail.documentedPath,
                hideo_route_surfaced: !!mail.hideoRouteSurfaced,
                package_path:         mail.packagePath || null,
                staff_concern:        mail.staffConcern ?? 0,
                staff_concern_events: [...(mail.staffConcernEvents || [])],
                record_package:       !!mail.record_package,
                record_tall_customer: !!mail.record_tall_customer,
                record_emorrow:       !!mail.record_emorrow,
                record_hideo_ps:      !!mail.record_hideo_ps,
                record_corridors:     !!mail.record_corridors,
                hideo_romance:        !!mail.hideoRomance,
                petra_romance:        !!mail.petraRomance,
                drops_pulled:         !!mail.dropsPulled,
                drops_pulled_by:      mail.dropsPulledBy || null,
                updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        },
        syncUserBakery(userId, bakery) {
            return _client.from('user_bakery_state').upsert({
                user_id: userId,
                bake_count: bakery.bakeCount ?? 0,
                upgrades: bakery.upgrades || {},
                ovens: bakery.ovens || [],
                unlocked: bakery.unlocked || ['cookie'],
                inventory: bakery.inventory || {},
                coins: bakery.coins ?? 0,
                updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        },
        syncUserTama(userId, tama) {
            return _client.from('user_tama_state').upsert({
                user_id: userId,
                max_slots: tama.maxSlots ?? 2,
                active_uids: tama.activeUids || [],
                updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        },
        syncUserQuiz(userId, quiz) {
            return _client.from('user_quiz_state').upsert({
                user_id: userId,
                done: !!quiz.done,
                score: quiz.score ?? 0,
                taken_at: quiz.done ? new Date().toISOString() : null,
                updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        },
        syncUserDaily(userId, state) {
            return _client.from('user_daily_state').upsert({
                user_id: userId,
                state: state || {},
                updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        },
        syncUserLogbookFavorites(userId, favorites) {
            const rows = (favorites || []).map(id => ({ user_id: userId, alien_id: id }));
            return this._syncCollection('user_logbook', userId, rows, 'user_id,alien_id', 'alien_id');
        },
        syncUserBunnymeals(userId, bunnymeals) {
            const rows = (bunnymeals || []).map(b => ({
                user_id: userId,
                bunnymeal_id: b.id,
                name: b.name || null,
                obtained_at: b.obtainedAt || Date.now(),
            }));
            return this._syncCollection('user_bunnymeals', userId, rows, 'user_id,bunnymeal_id', 'bunnymeal_id');
        },

        // ── Append-only points audit log. Callers must await or wrap in safeCall;
        // the supabase-js builder is a thenable, so the request is only sent
        // once .then() is invoked. Canonical balance lives in profiles.pts.
        logPtsTransaction(userId, delta, reason, balanceAfter) {
            return _client.from('pts_transactions').insert({
                user_id: userId,
                delta,
                reason,
                balance_after: balanceAfter,
            });
        },

        // ── Generic full-sync: replace all rows for one user with the local snapshot. ──
        // Used for collections that don't have a single mutation funnel (aliens, toys,
        // irl codes). Cheap at current scale; not suitable for tables with thousands of
        // rows per user.
        async _syncCollection(table, userId, rows, conflictKey, keepKey) {
            if (rows.length > 0) {
                const { error: upErr } = await _client.from(table).upsert(rows, { onConflict: conflictKey });
                if (upErr) return { error: upErr };
            }
            const keep = rows.map(r => r[keepKey]);
            let del = _client.from(table).delete().eq('user_id', userId);
            if (keep.length > 0) {
                const list = keep.map(u => `"${String(u).replace(/"/g, '\\"')}"`).join(',');
                del = del.not(keepKey, 'in', `(${list})`);
            }
            const { error: delErr } = await del;
            return { error: delErr || null };
        },

        syncUserToys(userId, toys) {
            const rows = toys.map(t => ({
                user_id: userId,
                toy_id: t.id,
                count: t.count ?? 1,
                name: t.name || null,
                img: t.img || null,
                description: t.desc || null,
            }));
            return this._syncCollection('user_toys', userId, rows, 'user_id,toy_id', 'toy_id');
        },

        syncUserIrlCodes(userId, codes) {
            const rows = codes.map(c => ({
                user_id: userId,
                item_id: c.id,
                name: c.name || null,
                code: c.code,
                icon: c.icon || null,
            }));
            return this._syncCollection('user_irl_codes', userId, rows, 'user_id,item_id', 'item_id');
        },

        // ── User aliens (relational, fully synced from S.myAliens after each save) ──
        async syncUserAliens(userId, aliens) {
            const rows = aliens.map(a => ({
                user_id: userId,
                uid: String(a.uid),
                alien_id: a.alienId || 'unknown',
                name: a.name || '?',
                nickname: a.nickname || null,
                alive: a.alive !== false,
                energy: a.energy ?? 100,
                hunger: a.hunger ?? 95,
                happiness: a.happiness ?? 100,
                last_fed: a.lastFed || 0,
                last_pet: a.lastPet || 0,
                last_played: a.lastPlayed || 0,
                last_slept: a.lastSlept || 0,
            }));
            if (rows.length > 0) {
                const { error: upErr } = await _client.from('user_aliens')
                    .upsert(rows, { onConflict: 'user_id,uid' });
                if (upErr) return { error: upErr };
            }
            // Drop rows the client no longer holds (alien released, profile reset).
            const keep = rows.map(r => r.uid);
            let del = _client.from('user_aliens').delete().eq('user_id', userId);
            if (keep.length > 0) {
                // PostgREST `in.(...)` syntax; values are quoted to be safe with text uids.
                const list = keep.map(u => `"${String(u).replace(/"/g, '\\"')}"`).join(',');
                del = del.not('uid', 'in', `(${list})`);
            }
            const { error: delErr } = await del;
            return { error: delErr || null };
        },

        // ── User missions (relational, dual-written alongside profiles.completed_missions / mission_link_opened) ──
        recordMissionEvent(missionKey, eventType) {
            return _client.rpc('record_mission_event', { p_mission_key: missionKey, p_event: eventType });
        },

        // ── User unlocks (relational, dual-written alongside profiles.unlocked_nicks/bgs) ──
        recordUnlock(userId, type, key) {
            return _client.from('user_unlocks').upsert({
                user_id: userId,
                unlock_type: type,
                unlock_key: key,
            }, { onConflict: 'user_id,unlock_type,unlock_key', ignoreDuplicates: true });
        },
        syncUserUnlocks(userId, nicks, bgs) {
            const rows = [
                ...nicks.map(key => ({ user_id: userId, unlock_type: 'nick', unlock_key: key })),
                ...bgs.map(key  => ({ user_id: userId, unlock_type: 'bg',   unlock_key: key })),
            ];
            if (!rows.length) return Promise.resolve();
            return _client.from('user_unlocks').upsert(rows, {
                onConflict: 'user_id,unlock_type,unlock_key',
                ignoreDuplicates: true,
            });
        },

        // ── User game scores (relational, dual-written alongside profiles.game_top_scores) ──
        upsertGameScore(userId, gameKey, score) {
            return _client.from('user_game_scores').upsert({
                user_id: userId,
                game_key: gameKey,
                score,
                achieved_at: new Date().toISOString(),
            }, { onConflict: 'user_id,game_key' });
        },

        // ── Games catalog ──
        loadGames() {
            return Promise.all([
                _client.from('games_registry').select('*').order('sort_order'),
                _client.from('games_config').select('*'),
            ]).then(([reg, cfg]) => ({
                registry: reg.error ? null : reg.data,
                configs:  cfg.error ? null : cfg.data,
                error:    reg.error || cfg.error || null,
            }));
        },
    };
})();

window.DB = DB;

// ═══════════════════════════════════════════════
//   SESSION I/O — load/save the entire user profile.
//   Sit on top of DB.* and bridge Supabase rows ↔ S.*.
// ═══════════════════════════════════════════════

// safeCall — unified error logging for Supabase calls.
// Returns the supabase result shape ({data, error}) so callers can destructure.
async function safeCall(label, fn, opts) {
    opts = opts || {};
    try {
        const res = await fn();
        if (res && res.error) {
            console.error(`[Konbini ${label}]`, res.error.message, res.error.details || '', res.error.hint || '');
            if (opts.toast !== false && window.toast) {
                window.toast(`${label} error: ${(res.error.message || 'unknown').slice(0, 60)}`);
            }
        }
        return res;
    } catch (e) {
        console.error(`[Konbini ${label} threw]`, e);
        return { data: null, error: e };
    }
}

async function loadProfile(userId) {
    // Fetch the slim profile row + every sidecar table in parallel. The
    // localOnly fields in persistence.js stay at their defaults until the
    // sidecar overrides land below.
    const [profileRes, scoresRes, unlocksRes, missionsRes, aliensRes, toysRes, irlRes, mailRes, bakeryRes, tamaRes, quizRes, dailyRes, favRes, bunnymealsRes] = await Promise.all([
        safeCall('loadProfile',    () => DB.loadProfile(userId),                { toast: false }),
        safeCall('loadScores',     () => DB.fetchUserScores(userId),            { toast: false }),
        safeCall('loadUnlocks',    () => DB.fetchUserUnlocks(userId),           { toast: false }),
        safeCall('loadMissions',   () => DB.fetchUserMissions(userId),          { toast: false }),
        safeCall('loadAliens',     () => DB.fetchUserAliens(userId),            { toast: false }),
        safeCall('loadToys',       () => DB.fetchUserToys(userId),              { toast: false }),
        safeCall('loadIrl',        () => DB.fetchUserIrlCodes(userId),          { toast: false }),
        safeCall('loadMail',       () => DB.fetchUserMail(userId),              { toast: false }),
        safeCall('loadBakery',     () => DB.fetchUserBakery(userId),            { toast: false }),
        safeCall('loadTama',       () => DB.fetchUserTama(userId),              { toast: false }),
        safeCall('loadQuiz',       () => DB.fetchUserQuiz(userId),              { toast: false }),
        safeCall('loadDaily',      () => DB.fetchUserDaily(userId),             { toast: false }),
        safeCall('loadFavorites',  () => DB.fetchUserLogbookFavorites(userId),  { toast: false }),
        safeCall('loadBunnymeals', () => DB.fetchUserBunnymeals(userId),        { toast: false }),
    ]);
    if (profileRes.error || !profileRes.data) return;
    applyLoadedRow(profileRes.data);

    if (!scoresRes.error && scoresRes.data) {
        S.gameTopScores = Object.fromEntries(scoresRes.data.map(r => [r.game_key, r.score]));
    }
    if (!unlocksRes.error && unlocksRes.data) {
        S.unlockedNicks = unlocksRes.data.filter(r => r.unlock_type === 'nick').map(r => r.unlock_key);
        S.unlockedBgs   = unlocksRes.data.filter(r => r.unlock_type === 'bg').map(r => r.unlock_key);
        if (!S.unlockedNicks.length) S.unlockedNicks = ['chronically'];
        if (!S.unlockedBgs.length)   S.unlockedBgs   = ['hills'];
        // early_bird is granted by the early_adopter side effect; preserve it
        // here in case user_unlocks doesn't have it yet (race after signup).
        if (S.data_early_adopter_flag && !S.unlockedNicks.includes('early_bird')) {
            S.unlockedNicks.push('early_bird');
        }
    }
    if (!missionsRes.error && missionsRes.data) {
        S.completedMissions = missionsRes.data.filter(r => r.completed_at).map(r => r.mission_key);
        S.missionLinkOpened = Object.fromEntries(
            missionsRes.data.filter(r => r.link_opened_at).map(r => [r.mission_key, true])
        );
    }
    if (!aliensRes.error && aliensRes.data) {
        S.myAliens = aliensRes.data.map(r => {
            const a = {
                uid: String(r.uid),
                alienId: r.alien_id,
                name: r.name,
                alive: r.alive,
                energy: r.energy,
                hunger: r.hunger,
                happiness: r.happiness,
                lastFed:    Number(r.last_fed)    || 0,
                lastPet:    Number(r.last_pet)    || 0,
                lastPlayed: Number(r.last_played) || 0,
                lastSlept:  Number(r.last_slept)  || 0,
            };
            if (r.nickname) a.nickname = r.nickname;
            return a;
        });
    }
    if (!toysRes.error && toysRes.data) {
        S.myToys = toysRes.data.map(r => ({
            id: r.toy_id, name: r.name, img: r.img, desc: r.description, count: r.count,
        }));
    }
    if (!irlRes.error && irlRes.data) {
        S.ownedIrl = irlRes.data.map(r => ({
            id: r.item_id, name: r.name, code: r.code, icon: r.icon,
        }));
    }
    if (!mailRes.error && mailRes.data) {
        const m = mailRes.data;
        S.mailFirstOpen        = m.first_open_at || null;
        S.mailStoryRead        = m.story_read || [];
        S.mailStoryChoices     = m.story_choices || {};
        S.mailStoryTrashed     = m.story_trashed || [];
        S.mailDailyState       = { ...defaultMailDailyState(), ...(m.daily_state || {}) };
        S.mailImportantTrashed = m.important_trashed || [];
        S.mailSpamState        = { ...defaultMailSpamState(), ...(m.spam_state || {}) };
        S.mailShopHealth       = (m.shop_health    ?? 70);
        S.mailClowAwareness    = (m.clow_awareness ?? 0);
        S.mailMysteryDepth     = (m.mystery_depth  ?? 0);
        S.mailRecordsKept      = (m.records_kept   ?? 0);
        S.mailTrustHideo       = m.trust_hideo     || 'mid';
        S.mailTrustPetra       = m.trust_petra     || 'mid';
        S.mailLastActiveDay    = (m.last_active_day ?? 0);
        S.mailFirstLoginDay    = (m.first_login_day ?? null);
        S.mailEmailStates      = m.email_states || {};
        S.mailEnding           = m.ending_key || null;
        S.playerVoiceWarm         = m.voice_warm           ?? 0;
        S.playerVoiceCurious      = m.voice_curious        ?? 0;
        S.playerVoiceProfessional = m.voice_professional   ?? 0;
        S.playerVoiceClipped      = m.voice_clipped        ?? 0;
        S.clippedRecoveryActive   = !!m.clipped_recovery;
        S.eMorrowSurfaced         = !!m.emorrow_surfaced;
        S.documentedPath          = !!m.documented_path;
        S.hideoRouteSurfaced      = !!m.hideo_route_surfaced;
        S.packagePath             = m.package_path || null;
        S.staffConcern            = m.staff_concern ?? 0;
        S.staffConcernEvents      = new Set(m.staff_concern_events || []);
        S.record_package          = !!m.record_package;
        S.record_tall_customer    = !!m.record_tall_customer;
        S.record_emorrow          = !!m.record_emorrow;
        S.record_hideo_ps         = !!m.record_hideo_ps;
        S.record_corridors        = !!m.record_corridors;
        S.hideoRomance            = !!m.hideo_romance;
        S.petraRomance            = !!m.petra_romance;
        S.dropsPulled             = !!m.drops_pulled;
        S.dropsPulledBy           = m.drops_pulled_by || null;
        // mailRecordsKept is now derived from the five booleans (v2 §15).
        S.mailRecordsKept         = (S.record_package ? 1 : 0)
                                  + (S.record_tall_customer ? 1 : 0)
                                  + (S.record_emorrow ? 1 : 0)
                                  + (S.record_hideo_ps ? 1 : 0)
                                  + (S.record_corridors ? 1 : 0);
    }
    if (!bakeryRes.error && bakeryRes.data) {
        const b = bakeryRes.data;
        S.bakeryBakeCount = b.bake_count || 0;
        S.bakeryUpgrades  = { ...S.bakeryUpgrades, ...(b.upgrades || {}) };
        S.bakeryUnlocked  = b.unlocked || ['cookie'];
        S.bakeryInventory = b.inventory || {};
        S.bakeryCoins     = b.coins || 0;
        const ovensRaw = b.ovens || [];
        S.bakeryOvens = [0, 1, 2, 3, 4].map(i => {
            const o = ovensRaw[i];
            if (!o || typeof o !== 'object') return { started: null, goodie: 'cookie', sel: 'cookie' };
            return { started: o.started || null, goodie: o.goodie || 'cookie', sel: o.sel || o.goodie || 'cookie' };
        });
    }
    if (!tamaRes.error && tamaRes.data) {
        const t = tamaRes.data;
        S.maxTamaSlots   = t.max_slots || 2;
        S.activeTamaUids = (t.active_uids || []).map(String);
    }
    if (!quizRes.error && quizRes.data) {
        S.clowriQuizDone  = !!quizRes.data.done;
        S.clowriQuizScore = quizRes.data.score || 0;
    }
    if (!dailyRes.error && dailyRes.data) {
        const ds = dailyRes.data.state || {};
        // Migrate legacy {1:{...}, 2:{...}} shape — pre-launch test data, just reset.
        const isLegacy = ds['1'] || ds['2'];
        S.dailyState = isLegacy
            ? { claimed: [], lastDate: '', finaleClaimed: false }
            : {
                claimed: Array.isArray(ds.claimed) ? ds.claimed.slice() : [],
                lastDate: ds.lastDate || '',
                finaleClaimed: !!ds.finaleClaimed,
            };
    }
    if (!favRes.error && favRes.data) {
        S.logbookFavorites = favRes.data.map(r => r.alien_id);
    }
    if (!bunnymealsRes.error && bunnymealsRes.data) {
        S.myBunnymeals = bunnymealsRes.data.map(r => ({
            id: r.bunnymeal_id,
            name: r.name,
            obtainedAt: r.obtained_at ? new Date(r.obtained_at).getTime() : Date.now(),
        }));
    }
}

let _saveTimer = null;

function scheduleSave() {
    clearTimeout(_saveTimer);
    _saveTimer = setTimeout(pushSave, CONFIG.SAVE_DELAY_MS);
}

async function pushSave() {
    const {data: {session} = {}} = await DB.getSession().catch(() => ({data:{}}));
    if (!session) return;
    const userId = session.user.id;
    const payload = {
        id: userId,
        username: S.profileData.name,
        updated_at: new Date().toISOString(),
        ...buildSavePayload()
    };
    const { error } = await safeCall('save', () => DB.upsertProfile(payload));
    if (error) return;
    // Sync relational sidecar tables. Fire-and-forget — local jsonb caches
    // already saved successfully and remain the read source of truth.
    safeCall('syncAliens',   () => DB.syncUserAliens(userId, S.myAliens),    { toast: false });
    safeCall('syncToys',     () => DB.syncUserToys(userId, S.myToys),        { toast: false });
    safeCall('syncIrlCodes', () => DB.syncUserIrlCodes(userId, S.ownedIrl),  { toast: false });
    safeCall('syncMail',     () => DB.syncUserMail(userId, {
        firstOpen: S.mailFirstOpen,
        storyRead: S.mailStoryRead,
        storyChoices: S.mailStoryChoices,
        storyTrashed: S.mailStoryTrashed,
        dailyState: S.mailDailyState,
        importantTrashed: S.mailImportantTrashed,
        spamState: S.mailSpamState,
        shopHealth:     S.mailShopHealth,
        clowAwareness:  S.mailClowAwareness,
        mysteryDepth:   S.mailMysteryDepth,
        recordsKept:    S.mailRecordsKept,
        trustHideo:     S.mailTrustHideo,
        trustPetra:     S.mailTrustPetra,
        lastActiveDay:  S.mailLastActiveDay,
        firstLoginDay:  S.mailFirstLoginDay,
        emailStates:    S.mailEmailStates,
        ending:         S.mailEnding,
        voiceWarm:           S.playerVoiceWarm,
        voiceCurious:        S.playerVoiceCurious,
        voiceProfessional:   S.playerVoiceProfessional,
        voiceClipped:        S.playerVoiceClipped,
        clippedRecovery:     S.clippedRecoveryActive,
        eMorrowSurfaced:     S.eMorrowSurfaced,
        documentedPath:      S.documentedPath,
        hideoRouteSurfaced:  S.hideoRouteSurfaced,
        packagePath:         S.packagePath,
        staffConcern:        S.staffConcern,
        staffConcernEvents:  S.staffConcernEvents,
        record_package:       S.record_package,
        record_tall_customer: S.record_tall_customer,
        record_emorrow:       S.record_emorrow,
        record_hideo_ps:      S.record_hideo_ps,
        record_corridors:     S.record_corridors,
        hideoRomance:         S.hideoRomance,
        petraRomance:         S.petraRomance,
        dropsPulled:          S.dropsPulled,
        dropsPulledBy:        S.dropsPulledBy,
    }), { toast: false });
    safeCall('syncBakery',   () => DB.syncUserBakery(userId, {
        bakeCount: S.bakeryBakeCount,
        upgrades: S.bakeryUpgrades,
        ovens: S.bakeryOvens,
        unlocked: S.bakeryUnlocked,
        inventory: S.bakeryInventory,
        coins: S.bakeryCoins,
    }), { toast: false });
    safeCall('syncTama',     () => DB.syncUserTama(userId, {
        maxSlots: S.maxTamaSlots,
        activeUids: S.activeTamaUids,
    }), { toast: false });
    safeCall('syncQuiz',     () => DB.syncUserQuiz(userId, {
        done: S.clowriQuizDone, score: S.clowriQuizScore,
    }), { toast: false });
    safeCall('syncDaily',    () => DB.syncUserDaily(userId, {
        claimed: S.dailyState.claimed,
        lastDate: S.dailyState.lastDate,
        finaleClaimed: S.dailyState.finaleClaimed,
    }), { toast: false });
    safeCall('syncFavorites',  () => DB.syncUserLogbookFavorites(userId, S.logbookFavorites),           { toast: false });
    safeCall('syncBunnymeals', () => DB.syncUserBunnymeals(userId, S.myBunnymeals),                     { toast: false });
    safeCall('syncUnlocks',    () => DB.syncUserUnlocks(userId, S.unlockedNicks, S.unlockedBgs),        { toast: false });
}

// Flush pending saves when the user leaves the page or switches
// away from the tab. beforeunload covers desktop tab-close;
// visibilitychange covers mobile app-switch (where beforeunload
// doesn't always fire).
window.addEventListener('beforeunload', () => {
    if (_saveTimer) {
        clearTimeout(_saveTimer);
        _saveTimer = null;
        try { pushSave(); } catch (e) {}
    }
});
window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && _saveTimer) {
        clearTimeout(_saveTimer);
        _saveTimer = null;
        try { pushSave(); } catch (e) {}
    }
});

window.safeCall = safeCall;
window.loadProfile = loadProfile;
window.scheduleSave = scheduleSave;
window.pushSave = pushSave;
